import { format } from "date-fns";
import {
  Calendar, Clock, Tag, Folder, ListChecks, Timer, Bell, Repeat,
  Pause, Ban, User, Layout, Columns,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { AppIcon } from "@/lib/appearanceIcons";
import { chipStyle } from "@/lib/appearancePalette";

// ─── Canonical My Tasks property presentation primitives ────────────────────
// One shared system used by BOTH TaskRow (lists) and TaskCard (Kanban). Only
// populated properties render; colour is always paired with an icon/label
// (never colour alone). Tones map to existing ContentOS theme tokens.

const SOLID = {
  neutral: "bg-muted text-muted-foreground",
  destructive: "bg-destructive/10 text-destructive",
  warning: "bg-warning/15 text-warning",
  success: "bg-success/12 text-success",
  accent: "bg-accent-subtle text-accent",
  primary: "bg-primary/10 text-primary",
  violet: "bg-secondary text-secondary-foreground",
};
const TEXT = {
  neutral: "text-muted-foreground", destructive: "text-destructive", warning: "text-warning",
  success: "text-success", accent: "text-accent", primary: "text-primary", violet: "text-secondary-foreground",
};

// PropertyChip — the single chip primitive (icon + optional label).
export function PropChip({ icon: Icon, children, tone = "neutral", solid = false, dot, title, testid }) {
  return (
    <span title={title} data-testid={testid}
      className={cn("inline-flex max-w-full items-center gap-1 whitespace-nowrap rounded-md px-1.5 py-0.5 text-[12px] font-medium leading-none",
        solid ? SOLID[tone] : "bg-transparent")}>
      {dot && <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", tone === "accent" ? "bg-accent" : `bg-${tone}`, tone === "neutral" && "bg-muted-foreground/40")} />}
      {Icon && <Icon className={cn("h-3 w-3 shrink-0", solid ? "" : TEXT[tone])} />}
      {children != null && <span className={cn("truncate", !solid && (tone === "neutral" ? "text-muted-foreground" : TEXT[tone]))}>{children}</span>}
    </span>
  );
}

// PropertyAvatar — assignee initial bubble with accessible tooltip.
export function PropAvatar({ name, testid }) {
  const initials = (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  return (
    <span title={name} data-testid={testid}
      className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-secondary text-[9px] font-semibold text-secondary-foreground ring-1 ring-border/60">
      {initials}
    </span>
  );
}

// ─── formatters (canonical, user-timezone via local Date) ───────────────────
const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
export function fmtDue(due_date) {
  const d = new Date(`${due_date}T00:00:00`);
  if (isNaN(d.getTime())) return { text: due_date, tone: "neutral" };
  const diff = Math.round((d - startOfToday()) / 86400000);
  if (diff === 0) return { text: "Today", tone: "accent" };
  if (diff === 1) return { text: "Tomorrow", tone: "neutral" };
  if (diff < 0) return { text: `Overdue ${-diff}d`, tone: "destructive" };
  return { text: format(d, "MMM d"), tone: "neutral" };
}
export function fmtTimeOfDay(hhmm) {
  const d = new Date(`2000-01-01T${hhmm}`);
  return isNaN(d.getTime()) ? hhmm : format(d, "h:mm a");
}
export function fmtDur(sec) {
  sec = Math.max(0, Math.round(sec || 0));
  const h = Math.floor(sec / 3600), m = Math.round((sec % 3600) / 60);
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  if (m) return `${m}m`;
  return sec > 0 ? "<1m" : "0m";
}

// ─── property registry ──────────────────────────────────────────────────────
// Each optional property: key, label, icon (for the Properties control), and a
// render(t, helpers) → chip node or null when not populated.
export const ALL_PROPS = [
  { key: "due", label: "Due date", icon: Calendar },
  { key: "dueTime", label: "Due time", icon: Clock },
  { key: "assignee", label: "Assignee", icon: User },
  { key: "subtasks", label: "Subtasks", icon: ListChecks },
  { key: "time", label: "Time", icon: Timer },
  { key: "tags", label: "Tags", icon: Tag },
  { key: "category", label: "Category", icon: Folder },
  { key: "reminder", label: "Reminder", icon: Bell },
  { key: "recurrence", label: "Recurrence", icon: Repeat },
  { key: "waiting", label: "Waiting / Blocked", icon: Pause },
  { key: "board", label: "Board", icon: Layout },
  { key: "stage", label: "Stage", icon: Columns },
];
export const DEFAULT_ORDER = ALL_PROPS.map((p) => p.key);

// Sensible per-view defaults (visible keys). Everything else stays hidden until
// the user enables it via the Properties control.
export const DEFAULT_VISIBLE = {
  today: ["due", "dueTime", "subtasks", "time", "waiting"],
  personal: ["due", "subtasks", "tags", "time"],
  assigned: ["due", "assignee", "subtasks", "category"],
  waiting: ["waiting", "due", "assignee", "subtasks"],
  completed: ["category", "time", "subtasks"],
  reminders: ["due", "dueTime", "reminder", "recurrence"],
  inbox: ["category", "due", "subtasks"],
  board: ["due", "assignee", "subtasks", "time", "tags"],
  all: ["due", "subtasks", "tags", "time", "category"],
};

export function defaultTaskCfg(ctx) {
  const vis = DEFAULT_VISIBLE[ctx] || DEFAULT_VISIBLE.all;
  return { density: "comfortable", subtaskMode: "summary", order: [...DEFAULT_ORDER], hidden: DEFAULT_ORDER.filter((k) => !vis.includes(k)) };
}

function renderProp(key, t, h) {
  const id = t.id;
  switch (key) {
    case "due": {
      if (!t.due_date) return null;
      const { text, tone } = fmtDue(t.due_date);
      const time = t.due_time && !h.visible.has("dueTime") ? ` · ${fmtTimeOfDay(t.due_time)}` : "";
      return <PropChip key={key} icon={Calendar} tone={tone} solid={tone !== "neutral"} testid={`prop-due-${id}`}>{text}{time}</PropChip>;
    }
    case "dueTime":
      return t.due_time ? <PropChip key={key} icon={Clock} testid={`prop-duetime-${id}`}>{fmtTimeOfDay(t.due_time)}</PropChip> : null;
    case "assignee": {
      if (h.ctx === "personal" || !h.members?.length) return null;
      const m = h.members.find((x) => x.id === t.owner_id);
      if (!m) return null;
      return <PropAvatar key={key} name={m.name || m.full_name || m.email} testid={`prop-assignee-${id}`} />;
    }
    case "subtasks": {
      const sp = t.subtask_progress;
      if (!sp?.total) return null;
      const done = sp.done >= sp.total;
      return <PropChip key={key} icon={ListChecks} tone={done ? "success" : "neutral"} testid={`prop-subtasks-${id}`}>{sp.done}/{sp.total}</PropChip>;
    }
    case "time": {
      const tracked = t.tracked_seconds || 0;
      const est = (t.estimate_minutes || 0) * 60;
      const running = h.running?.(id);
      if (!tracked && !est && !running) return null;
      let text;
      if (tracked > 0) text = est ? `${fmtDur(tracked)} / ${fmtDur(est)}` : fmtDur(tracked);
      else if (running) text = "Running";
      else text = `Est ${fmtDur(est)}`;
      return (
        <span key={key} title={running ? "Timer running" : `${fmtDur(tracked)} tracked${est ? ` · Est ${fmtDur(est)}` : ""}`}
          data-testid={`prop-time-${id}`}
          className={cn("inline-flex items-center gap-1 whitespace-nowrap rounded-md px-1.5 py-0.5 text-[12px] font-medium leading-none",
            running ? "bg-accent-subtle text-accent" : "text-muted-foreground")}>
          {running && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-accent" data-testid={`prop-running-${id}`} />}
          <Timer className={cn("h-3 w-3", running ? "text-accent" : "text-muted-foreground")} />
          {text}
        </span>
      );
    }
    case "tags": {
      const tags = t.tags || [];
      if (!tags.length) return null;
      const shown = tags.slice(0, 2);
      return (
        <span key={key} className="inline-flex items-center gap-1" data-testid={`prop-tags-${id}`}>
          {shown.map((tg) => <PropChip key={tg} icon={Tag} solid tone="neutral" testid={`prop-tag-${id}-${tg}`}>{tg}</PropChip>)}
          {tags.length > 2 && <PropChip solid tone="neutral" title={tags.slice(2).join(", ")} testid={`prop-tags-more-${id}`}>+{tags.length - 2}</PropChip>}
        </span>
      );
    }
    case "category": {
      const name = h.catName?.(t.category_id);
      if (!name) return null;
      const cat = h.cats?.find((c) => c.id === t.category_id);
      const color = cat?.color;
      return (
        <span key={key} data-testid={`prop-category-${id}`}
          className={cn("inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[12px] font-medium leading-none", !color && "border-transparent bg-muted text-muted-foreground")}
          style={chipStyle(color)}>
          {cat?.icon ? <AppIcon name={cat.icon} className="h-3 w-3" /> : <span className="h-2 w-2 shrink-0 rounded-full" style={color ? { backgroundColor: color } : undefined} />}
          <span className="truncate">{name}</span>
        </span>
      );
    }
    case "reminder":
      return t.reminders?.length ? <PropChip key={key} icon={Bell} tone="accent" title="Reminder set" testid={`prop-reminder-${id}`} /> : null;
    case "recurrence":
      return t.recurrence_summary ? <PropChip key={key} icon={Repeat} tone="violet" solid title={t.recurrence_summary} testid={`prop-recurrence-${id}`}>{h.density === "detailed" ? t.recurrence_summary : null}</PropChip> : null;
    case "waiting": {
      if (t.blocked_reason) return <PropChip key={key} icon={Ban} tone="destructive" solid title={t.blocked_reason} testid={`prop-blocked-${id}`}>Blocked</PropChip>;
      if (t.waiting) return <PropChip key={key} icon={Pause} tone="warning" solid testid={`prop-waiting-${id}`}>Waiting</PropChip>;
      return null;
    }
    case "board":
      return t.board_name ? <PropChip key={key} icon={Layout} testid={`prop-board-${id}`}>{t.board_name}</PropChip> : null;
    case "stage":
      return t.stage ? <PropChip key={key} icon={Columns} testid={`prop-stage-${id}`}>{t.stage_name || t.stage}</PropChip> : null;
    default:
      return null;
  }
}

// TaskProperties — renders the configured, populated properties for a task.
export function TaskProperties({ t, ctx = "all", density = "comfortable", cfg, catName, cats, members, running, max = 99 }) {
  const conf = cfg || defaultTaskCfg(ctx);
  const hidden = new Set(conf.hidden || []);
  const order = (conf.order || DEFAULT_ORDER).filter((k) => !hidden.has(k));
  const visible = new Set(order);
  const h = { ctx, density, catName, cats, members, running, visible };
  const nodes = [];
  for (const key of order) {
    const node = renderProp(key, t, h);
    if (node) nodes.push(node);
    if (nodes.length >= max) break;
  }
  if (!nodes.length) return null;
  return <div className="flex min-w-0 flex-wrap items-center gap-1.5">{nodes}</div>;
}

export const PRIORITY_META = {
  urgent: { tone: "destructive", label: "Urgent" },
  high: { tone: "warning", label: "High" },
  normal: { tone: "neutral", label: "Normal" },
  low: { tone: "neutral", label: "Low" },
};
